// @flow
import React, { useCallback, useContext, useMemo, Fragment } from 'react';
import { Grid } from 'semantic-ui-react';
import { DimensionsConfigUsageContainer } from '@components/custom-discounts/dimensions-configuration/usage-dimension/DimensionsConfigUsageContainer';
import { useIsUserAdmin } from '@src/custom-hooks/useIsUserAdmin';
import { useEstimateMLDForFieldGuidance } from '@src/custom-hooks/useEstimateMLDForFieldGuidance';
import {
  getDropdownOptionsFromArray,
  getIfGivenRowIsANewlyAddedRow,
} from '@src/common-utils/utils';
import { If } from '@presentational/If';

import { DIMENSIONS_FOR_WHICH_MULTI_SELECT_IS_ENABLED } from '../../../components/common/vbd/constants';
import {
  FieldArrayGridConfigItemContext,
  FieldArrayGridSelectField,
  isAnyOfDependenciesNotSet,
} from '../../../formik-utils/FieldArrayGrid';
import { DataFetcherContainer } from '../../presentational/DataFetcherContainer';
import { IS_ROW_FROZEN, RATE_CARD_BACKEND_NAME } from '../../../constants';
import { getDropdownOptions } from '../../stream-governance/utils';
import {
  getDimensionsColumnConfig,
  getIfAllTheParentValuesOfGivenDimensionColumnForGivenMLDRowAreFilled,
  getSpecialMLDConfigForGivenMetricAndDimension,
} from '../utils';
import { useGetDependentDimensionsCombinationsQuery } from '../../../service-definitions/estimates';
import { EstimateContext } from '../../../contexts/EstimateContext';
import {
  getDimensionsOptionsByAddingDisabledForFieldBasedOnGuidanceAndUsage,
  shouldShowUsageContainerForGivenMetric,
} from '../mld-for-field/utils';

import { DimensionsConfigDiscountContainer } from './DimensionsConfigDiscountContainer';

const getDropdownOptionsForTheGivenMetricDims = (
  metricName,
  rowValues,
  columnConfig,
  newDimCombs,
  useMultiSelectWithCheckboxes
) => {
  // if the parent values are not set, then return [] else go and actually calculate the dropdown options
  const areAllParentSet = getIfAllTheParentValuesOfGivenDimensionColumnForGivenMLDRowAreFilled(
    columnConfig,
    rowValues
  );

  if (!areAllParentSet) {
    return [];
  }

  // Check if we have any special static config defined for this metric and dims
  const specialConfig = getSpecialMLDConfigForGivenMetricAndDimension(
    metricName,
    columnConfig.backendFieldName
  );

  if (specialConfig?.dropDownStaticValues) {
    return getDropdownOptionsFromArray(specialConfig.dropDownStaticValues, true);
  }

  // ALL dropdown option is not added here for multi-select dims or when the metric+dim special config says not to.
  const doNotAddAllDropdownOption =
    useMultiSelectWithCheckboxes || specialConfig?.doNotAddAllDropdownOption;

  return getDropdownOptions(
    newDimCombs,
    rowValues,
    columnConfig,
    false,
    false,
    true,
    true,
    [],
    false,
    !doNotAddAllDropdownOption
  );
};

export const DependentDimensionsDropdownContainer = () => {
  const estimate = useContext(EstimateContext);
  const { columnsConfiguration, parentContext, rowValues, readOnlyMode } = useContext(
    FieldArrayGridConfigItemContext
  );

  const isUserAdmin = useIsUserAdmin();
  const mldGuidance = useEstimateMLDForFieldGuidance();

  const dimensionColsConfig = getDimensionsColumnConfig(columnsConfiguration);

  const rateCardVersion = estimate.inputs[RATE_CARD_BACKEND_NAME];
  const metricName = parentContext.metricName;

  return (
    <DataFetcherContainer
      dataFetchingArgs={useMemo(
        () => [
          {
            metricName: metricName,
            rateCardVersion: rateCardVersion,
          },
        ],
        [metricName, rateCardVersion]
      )}
      dataFetchingFunction={useCallback(useGetDependentDimensionsCombinationsQuery, [])}
    >
      {(dataUniverse) => {
        if (!dataUniverse) {
          return null;
        }
        const dimensionCombinations = dataUniverse?.dimension_combinations ?? [];
        const newDimCombs = [];

        // todo:: Discuss this with BE team and ensure this is done in BE
        for (const dimComb of dimensionCombinations) {
          const temp = { ...dimComb };
          for (const key of Object.keys(temp)) {
            if (key.includes('_name')) {
              const newKey = key.replace('_name', '');
              temp[newKey] = temp[key];
            }
          }
          newDimCombs.push(temp);
        }

        return (
          <>
            {dimensionColsConfig.map((columnConfig) => {
              const useMultiSelectWithCheckboxes =
                DIMENSIONS_FOR_WHICH_MULTI_SELECT_IS_ENABLED.includes(
                  columnConfig.backendFieldName
                );

              let dropdownOptions = getDropdownOptionsForTheGivenMetricDims(
                metricName,
                rowValues,
                columnConfig,
                newDimCombs,
                useMultiSelectWithCheckboxes
              );

              /*
              For non-admin users, disable options not in both usages AND guidance array
              do this only for newly added rows.
              
              We don't have to check for frozen rows, even while they are being edited,
              as we only allow the user to edit the discount field and other dimensions are frozen.
              */
              if (!isUserAdmin && getIfGivenRowIsANewlyAddedRow(rowValues)) {
                dropdownOptions =
                  getDimensionsOptionsByAddingDisabledForFieldBasedOnGuidanceAndUsage({
                    metricName,
                    rowValues,
                    dropdownOptions,
                    mldGuidance,
                    columnConfig,
                  });
              }

              return (
                <Fragment key={columnConfig.backendFieldName}>
                  <Grid.Column width={columnConfig.columnWidth}>
                    <FieldArrayGridSelectField
                      columnConfig={columnConfig}
                      disabled={
                        readOnlyMode ||
                        isAnyOfDependenciesNotSet(columnConfig, rowValues) ||
                        rowValues[IS_ROW_FROZEN]
                      }
                      key={columnConfig.backendFieldName}
                      options={dropdownOptions}
                      useMultiSelectWithCheckboxes={useMultiSelectWithCheckboxes}
                    />
                  </Grid.Column>
                </Fragment>
              );
            })}
            <If test={shouldShowUsageContainerForGivenMetric(metricName)}>
              <Grid.Column verticalAlign="middle" width={2}>
                <DimensionsConfigUsageContainer />
              </Grid.Column>
            </If>
            <Grid.Column width={2}>
              <DimensionsConfigDiscountContainer />
            </Grid.Column>
          </>
        );
      }}
    </DataFetcherContainer>
  );
};
